import * as z from 'zod';

import { LosId } from '../../BrandedIds';
import { add } from '../../utils/math';
import { DateString, MonetaryValue } from '../fields';

const HISTORICAL_PAYMENT_BUCKET_KEYS = [
  'principal',
  'additionalPrincipal',
  'interest',
  'escrow',
  'suspense',
  'fee',
  'lateFee',
  'advance',
  'reserve',
  'holdback',
  'other',
] as const;

export const HistoricalPaymentRow = z
  .object({
    loanId: LosId,
    // Blank cells normalize to undefined so they store as NULL instead of ""
    // colliding in the (loan_id, transaction_id) unique index.
    transactionId: z
      .string()
      .transform((value) => value.trim() || undefined)
      .optional(),
    paymentDate: DateString,
    dueDate: DateString.optional(),
    method: z.string().optional(),
    description: z.string().optional(),
    total: MonetaryValue,
    upb: MonetaryValue.optional(),
    principal: MonetaryValue.optional(),
    additionalPrincipal: MonetaryValue.optional(),
    interest: MonetaryValue.optional(),
    escrow: MonetaryValue.optional(),
    suspense: MonetaryValue.optional(),
    fee: MonetaryValue.optional(),
    lateFee: MonetaryValue.optional(),
    advance: MonetaryValue.optional(),
    reserve: MonetaryValue.optional(),
    holdback: MonetaryValue.optional(),
    other: MonetaryValue.optional(),
  })
  .superRefine((data, ctx) => {
    const hasNonZeroBucket = HISTORICAL_PAYMENT_BUCKET_KEYS.some((key) => data[key]);
    if (!hasNonZeroBucket) return;

    const bucketSum = HISTORICAL_PAYMENT_BUCKET_KEYS.reduce((sum, key) => add(sum, data[key] ?? 0), 0);
    const total = add(data.total);
    if (bucketSum !== total) {
      ctx.addIssue({
        code: 'custom',
        message: `Total (${total}) must equal the sum of the accounting bucket columns (${bucketSum})`,
        path: ['total'],
      });
    }
  });

export type HistoricalPaymentRow = z.infer<typeof HistoricalPaymentRow>;

export const HISTORICAL_PAYMENT_ROW_REQUIRED_FIELDS = Object.keys(HistoricalPaymentRow.shape).filter(
  (key) => !HistoricalPaymentRow.shape[key as keyof typeof HistoricalPaymentRow.shape].isOptional(),
);
